import React, { useState, useRef, useEffect, useMemo } from "react";
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid'
import classnames from 'classnames'
import { getInputStyles } from './styles'

interface ComboboxItem {
    id: string | number | null
    name: string
    description?: string | React.ReactNode
}

interface PropTypes {
    className?: string
    data: Array<ComboboxItem>
    value: any
    handleChange: (value: any) => void
    label?: string | React.ReactNode
    labelPlural?: string
    wildCardDisabled?: boolean
    autoFocus?: boolean
    placeholder?: string
    isNotFilter?: boolean
    disabled?: boolean
    description?: string
    maxVisibleItems?: number
}

export const ComboboxAutocomplete: React.FC<PropTypes> = React.memo((props) => {
    const {
        data,
        value,
        label,
        handleChange,
        labelPlural = 'items',
        wildCardDisabled = false,
        autoFocus,
        placeholder,
        isNotFilter = false,
        disabled,
        className,
        description,
        maxVisibleItems = 50,
    } = props
    const inputEl = useRef<HTMLInputElement>(null)
    // Tracks whether the user has typed during the current open session.
    // Used to distinguish "fresh open, input empty" (show wildcard) from
    // "user typed then cleared" (hide wildcard).
    const hasTypedRef = useRef(false)

    // Allow the input to be focused on mount if autoFocus is true
    useEffect(() => {
        if ((autoFocus ?? false) && inputEl.current != null) {
            inputEl.current.focus()
        }
    }, [autoFocus])

    // Memoize the wildcard item to prevent recreation on every render
    const wildcardItem: ComboboxItem = useMemo(() => ({
        id: null,
        name: `All ${labelPlural.toLowerCase()}`,
        description: `Select all ${labelPlural.toLowerCase()}`,
    }), [labelPlural])

    // Memoize the data with wildcard item to prevent recreation on every render
    const dataWithWildcardItem = useMemo(() =>
        !wildCardDisabled ? [wildcardItem, ...data] : data,
        [wildCardDisabled, wildcardItem, data]
    )

    const [query, setQuery] = useState<string | null>(null)

    // Memoize the selected item to prevent recalculation on every render
    const selected = useMemo(() => {
        if (wildCardDisabled) {
            return dataWithWildcardItem.find((item) => item.id === value) ?? null
        }
        return dataWithWildcardItem.find((item) => item.id === value) ?? wildcardItem
    }, [dataWithWildcardItem, value, wildCardDisabled, wildcardItem])

    // Memoize filtered items with virtualization
    const { filteredItems, totalFilteredCount } = useMemo(() => {
        // Show wildcard only on fresh open (query=null) or before the user has typed anything (query='' && !hasTyped).
        // Once the user has typed, hide the wildcard even if they clear back to empty.
        const showWildcard = query === null || (query === '' && !hasTypedRef.current)
        const items = showWildcard
            ? dataWithWildcardItem
            : data.filter((item) => {
                if (query === '') return true
                const itemName = item.name.toLowerCase().replace(/\s+/g, '')
                const searchQuery = query.toLowerCase().replace(/\s+/g, '')
                return itemName.includes(searchQuery)
            })

        // Limit visible items for performance
        return {
            filteredItems: items.slice(0, maxVisibleItems),
            totalFilteredCount: items.length
        }
    }, [dataWithWildcardItem, data, query, maxVisibleItems])

    return (
        <div className={classnames('w-full', className)}>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate text-left">
                <div className="flex flex-col">
                    <div>{label}</div>
                    {description != null && <span className="text-xs text-slate-500 dark:text-slate-400">{description}</span>}
                </div>
            </label>
            <Combobox
                immediate
                value={selected}
                onChange={(option) => {
                    if (option !== null) {
                        setQuery(null)
                        hasTypedRef.current = false
                        handleChange(option.id ?? null)
                    }
                }}
                disabled={disabled}
                onClose={() => {
                    setQuery(null)
                    hasTypedRef.current = false
                }}
            >
                {({ open }) => (
                <div className={classnames('relative', open && 'z-[100]')}>
                    <ComboboxInput
                        value={query ?? (selected?.name ?? '')}
                        onFocus={() => {
                            // Clear the displayed selection text so the user types into an empty field.
                            // Only for filter mode (not isNotFilter) and only on fresh open (query=null).
                            // isNotFilter fields show the selected name at rest and must not blank on focus.
                            if (query === null && !isNotFilter) setQuery('')
                        }}
                        onChange={(event) => {
                            hasTypedRef.current = true
                            setQuery(event.target.value)
                        }}
                        className={classnames(getInputStyles())}
                        displayValue={(item: any) => item?.name}
                        placeholder={placeholder ?? (typeof label === 'string' ? 'Search for a(n) ' + label + '...' : 'Search ' + labelPlural.toLowerCase() + '...')}
                    />
                    <ComboboxButton
                        className={classnames(
                            'absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none border-0',
                            disabled === true ? 'cursor-not-allowed' : ''
                        )}
                    >
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </ComboboxButton>

                    {/* Portal the list so overflow-clipped tab panels cannot scroll the page. */}
                    <ComboboxOptions
                        anchor={{ to: 'bottom start', gap: 4 }}
                        portal
                        modal={false}
                        transition
                        className="z-[200] max-h-60 w-[var(--input-width)] overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 pl-0 text-base shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none sm:text-sm transition duration-100 ease-in data-[closed]:opacity-0"
                    >
                            {filteredItems.length === 0 && query !== '' ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">{`No ${labelPlural.toLowerCase()} found`}</div>
                            ) : (
                                <>
                                    {filteredItems.map((item) => (
                                        <ComboboxOption
                                            key={item.id}
                                            value={item}
                                            className="relative cursor-default select-none py-2 pl-10 pr-4 text-gray-900 dark:text-gray-100 data-[focus]:bg-indigo-600 data-[focus]:text-white data-[focus]:outline-none data-[focus]:font-semibold"
                                        >
                                            {({ selected }) => (
                                                <>
                                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{item.name}</span>
                                                    {selected ? (
                                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-500 dark:text-indigo-500">
                                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                        </span>
                                                    ) : null}
                                                    {item.description != null && item.description !== '' && (
                                                        <span className={`text-xs ${selected ? 'font-medium' : 'font-normal'} overflow-auto w-60 whitespace-normal break-words`}>
                                                            {item.description}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </ComboboxOption>
                                    ))}
                                    {totalFilteredCount > maxVisibleItems && (
                                        <div className="relative cursor-default select-none py-2 px-4 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                                            Showing {maxVisibleItems} of {totalFilteredCount} {labelPlural.toLowerCase()}. Type to search for more.
                                        </div>
                                    )}
                                </>
                            )}
                    </ComboboxOptions>
                </div>
                )}
            </Combobox>
        </div>
    )
})

export default ComboboxAutocomplete;
